/** @jsxImportSource @emotion/react */
import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { RoutineSectionItem } from "@packages/sdk";
import { RoutineItemReferenceType, snakeCase } from "@packages/sdk";
import type { ContentMediumProps } from "@packages/ui";
import {
  ChallengeCheckmark,
  PlayLink,
  spacing,
  Tag,
  VideoIcon,
} from "@packages/ui";

import type { BaseGetSectionItemProps } from "./Section.emotion";

const timeTagStyle: Interpolation = {
  position: "absolute",
  top: spacing.s,
  right: spacing.s,
};

export const getRoutineSectionItemProps = ({
  analytics,
  item,
  itemTypeLinkSearchParams = {},
  section,
  link,
  page,
  screenName,
  t,
  format,
}: BaseGetSectionItemProps<RoutineSectionItem>): ContentMediumProps => {
  let href;
  switch (item?.reference?.reference_type) {
    case RoutineItemReferenceType.CHALLENGE:
    case RoutineItemReferenceType.COLLECTION:
      href = `/collections/${item.reference.reference_id}`;
      break;
    case RoutineItemReferenceType.PRAYER:
      href = `/prayers/${item.reference.reference_id}`;
      break;
    case RoutineItemReferenceType.ACTIVITY:
      if (item.reference.reference_id === 1) {
        href = `/trivia`;
      } else {
        href = `/the-word`;
      }
      break;
    case RoutineItemReferenceType.REMINDER:
    default:
      // also remove link when href is not set, so we avoid including anything link-adjacent
      link = false;
  }

  let description = null;
  switch (item?.reference?.reference_type) {
    case RoutineItemReferenceType.CHALLENGE:
    case RoutineItemReferenceType.COLLECTION:
      description = item.reference.reference.short_desc;
      break;
    case RoutineItemReferenceType.PRAYER:
      description = item.reference.reference.label_desc;
      break;
    case RoutineItemReferenceType.ACTIVITY:
    case RoutineItemReferenceType.REMINDER:
      description = item.reference.reference.subtitle;
      break;
    default:
  }

  let metadata = null;
  switch (item?.reference?.reference_type) {
    case RoutineItemReferenceType.CHALLENGE:
    case RoutineItemReferenceType.COLLECTION:
      metadata = item.reference.reference.sessions;
      break;
    case RoutineItemReferenceType.PRAYER:
      metadata = item.reference.reference.lengths;
      break;
    case RoutineItemReferenceType.ACTIVITY:
      metadata = item.reference.reference?.game
        ? t("x_playing_plural", {
            x_playing_plural: item.reference.reference.game.player_count,
          })
        : null;
      break;
    case RoutineItemReferenceType.REMINDER:
    default:
  }

  let metadataInlineIcon = null;
  if (
    (item?.reference?.reference_type === RoutineItemReferenceType.CHALLENGE ||
      item?.reference?.reference_type === RoutineItemReferenceType.COLLECTION ||
      item?.reference?.reference_type === RoutineItemReferenceType.PRAYER) &&
    item.reference.reference.has_video
  ) {
    metadataInlineIcon = <VideoIcon.Filled />;
  }

  let requiresSubscription = false;
  if (
    item?.reference?.reference_type === RoutineItemReferenceType.CHALLENGE ||
    item?.reference?.reference_type === RoutineItemReferenceType.COLLECTION ||
    item?.reference?.reference_type === RoutineItemReferenceType.PRAYER
  ) {
    requiresSubscription = !item.reference.reference.has_access;
  }

  const timeFormat = Intl.DateTimeFormat(undefined, {
    timeStyle: "short",
  }).resolvedOptions();
  let imageContainerChildren = null;
  if (item?.reference?.session?.is_completed) {
    imageContainerChildren = <ChallengeCheckmark />;
  } else if (
    item?.reference?.session?.timestamp &&
    !screenName?.includes("routine") &&
    !!format
  ) {
    // don't show timestamps on routine page, where timestamps are already visible
    imageContainerChildren = (
      <Tag css={timeTagStyle}>
        {format(
          new Date(item.reference.session.timestamp),
          timeFormat.hour12 ? "hh:mm a" : "HH:mm",
        )}
      </Tag>
    );
  }

  let children = null;
  const onChildClickCapture = () => {
    analytics?.track({
      event: HallowAnalyticsEvent.TappedSectionItem,
      properties: {
        // Refactor: This event should be logged for every Section Item (move event up DOM tree)
        page_id: page?.id,
        page_title: snakeCase(page?.title) ?? null,
        page_slug: page?.slug ?? null,
        screen_name: screenName ?? null,
        section_id: section?.id,
        section_item_id: item?.item_id ?? null,
        section_title: section?.title ?? null,
        section_type: section?.type ?? null,
        index: section?.items.indexOf(item) ?? null,
        item_type: item?.type ?? null,
        section_cohort_id: section?.cohorts?.map((c) => c.id) ?? null,
        section_cohort: section?.cohorts?.map((c) => c.name) ?? null,
      },
    });
  };
  const targetUrl = `${href}?${new URLSearchParams(itemTypeLinkSearchParams[item?.reference_type]).toString()}`;
  if (item?.reference?.session?.prayer_id) {
    children = (
      <PlayLink
        href={targetUrl}
        playable={{
          type: "prayer",
          id: item.reference.session.prayer_id,
        }}
        onClickCapture={onChildClickCapture}
      />
    );
  } else if (link) {
    children = <Link href={targetUrl} onPointerUp={onChildClickCapture} />;
  }

  return {
    asChild: !!children,
    children,
    description,
    imgContainerProps: {
      children: imageContainerChildren,
    },
    imgSrc: item?.reference?.reference?.images,
    subscription: requiresSubscription,
    metadata,
    metadataInlineIcon,
    title: item?.reference?.reference?.title,
    color: (item?.reference?.reference?.images?.color_hex ??
      "#000000") as `#${string}`,
  };
};
